import React from 'react';
import axis  from '../../media/axis_logo.png';
import vtech from '../../media/vtech_logo.png';
import { buildGradeString } from '../../utils/calculations';

const logoMap = {
    axis:  { image: axis,  name: 'Axis Diamond Tools'  },
    vtech: { image: vtech, name: 'V-TECH INDUSTRIES'   },
};

const OPERATIONS = [
    'Sintering', 'First Inspection', 'Filing', 'Sandblasting', 'Straightening',
    'Grinding Width', 'Straightening', 'Milling Height', 'Buffing', 'Chamfering',
    'Cutting Length', 'Buffing', 'Straightening', 'Etching', 'First Inspection',
];

const r = (n, d = 3) => (typeof n === 'number' ? n.toFixed(d) : '—');

/**
 * ProcessSheetTable
 *
 * Props:
 *   inputs      — raw saved inputs ({ customerName, batchNumber, gritCode, bondType, concentration, ... }) })
 *   computed    — output of computeSheet()
 *   sheetNumber — string like "PS-2604-0001", or null when not yet saved
 *   logo        — 'axis' | 'vtech'
 */
const ProcessSheetTable = ({ inputs, computed, sheetNumber, logo = 'axis' }) => {
    const currentLogo = logoMap[logo] || logoMap.axis;

    const gradeString = buildGradeString({
        gritCode: inputs.gritCode || '',
        bondType:      inputs.bondType      || '',
        concentration: inputs.concentration || '',
    });

    const currentDate = new Date().toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
    });

    const totalStonesPerMould  = parseInt(inputs.sticksPerMould || 0) * parseInt(inputs.stonesPerStick || 0);
    const copperBarTotal       = parseInt(inputs.numberOfMoulds || 0) * parseInt(inputs.sticksPerMould || 0) * parseInt(inputs.stonesPerStick || 0);

    return (
        <table className="w-full border-collapse border border-gray-400 text-sm eprint">
            <tbody>

                {/* Row 1 — Logo | Company | S.No. */}
                <tr>
                    <td rowSpan="2" colSpan="3" className="border border-gray-400 text-center p-2">
                        <img src={currentLogo.image} alt={currentLogo.name} className="h-14 mx-auto" />
                    </td>
                    <td rowSpan="2" colSpan="3" className="border border-gray-400 text-center p-2 text-xs leading-relaxed">
                        <strong className="uppercase block">{currentLogo.name}</strong>
                        Plot no. 70, Sector 5, IMT Manesar<br />Gurugram, Haryana 122050
                    </td>
                    <td colSpan="3" className="border border-gray-400 p-2 text-xs">S. No.</td>
                </tr>

                {/* Row 2 — Process No. */}
                <tr>
                    <td colSpan="3" className="border border-gray-400 p-2 text-xs">
                        Process No.&nbsp;
                        {sheetNumber
                            ? <strong>{sheetNumber}</strong>
                            : <span className="text-gray-400 italic">pending save</span>
                        }
                    </td>
                </tr>

                {/* Row 3 — Title */}
                <tr>
                    <td colSpan="9" className="border border-gray-400 text-center p-3">
                        <h2 className="text-xl font-bold tracking-wide">Process Sheet</h2>
                    </td>
                </tr>

                {/* Row 4 — Job info */}
                <tr>
                    <td colSpan="9" className="border border-gray-400 p-3">
                        <div className="text-right text-xs text-gray-400 mb-1">Date: {currentDate}</div>
                        <table className="w-11/12 mx-auto border-none text-sm">
                            <tbody>
                                <tr>
                                    <td className="w-1/2 py-0.5">Customer: <strong>{inputs.customerName}</strong></td>
                                    <td className="py-0.5">Batch No.: <strong>{inputs.batchNumber}</strong></td>
                                </tr>
                                <tr>
                                    <td className="py-0.5">Grade: <strong>{gradeString}</strong></td>
                                    <td className="py-0.5">Mould Size: <strong>{inputs.mouldWidth} × {inputs.mouldLength} mm</strong></td>
                                </tr>
                                <tr>
                                    <td className="py-0.5">Size: <strong>{inputs.stoneSize}</strong></td>
                                    <td className="py-0.5">
                                        Stones/mould: <strong>{inputs.sticksPerMould} × {inputs.stonesPerStick} = {totalStonesPerMould}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="py-0.5">Total stones: <strong>{computed?.totalStones ?? '—'}</strong></td>
                                    <td className="py-0.5">Moulds to process: <strong>{inputs.numberOfMoulds}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                </tr>

                {/* Row 5 — Column headers */}
                <tr>
                    <th colSpan="3" className="border border-gray-400 text-center p-2">RAW MATERIAL PLANNING</th>
                    <th colSpan="2" className="border border-gray-400 text-center p-2">ABRASIVE [gm]</th>
                    <th colSpan="2" className="border border-gray-400 text-center p-2">BOND [gm]</th>
                    <th colSpan="2" className="border border-gray-400 text-center p-2">TOTAL [gm]</th>
                </tr>

                {/* Row 6 — Per-cup weights */}
                <tr>
                    <td colSpan="3" className="border border-gray-400 text-center p-2">Weight for each cup</td>
                    <td colSpan="2" className="border border-gray-400 text-center p-2 font-bold">{r(computed?.abrasiveWeightPerCup)}</td>
                    <td colSpan="2" className="border border-gray-400 text-center p-2 font-bold">{r(computed?.bondWeightPerCup)}</td>
                    <td colSpan="2" className="border border-gray-400 text-center p-2 font-bold">
                        {computed ? r(computed.abrasiveWeightPerCup + computed.bondWeightPerCup) : '—'}
                    </td>
                </tr>

                {/* Row 7+8 — Per-batch totals (rowspan split for kryolite / pure bond) */}
                <tr>
                    <td rowSpan="2" colSpan="3" className="border border-gray-400 text-center p-2">
                        Total weight for <strong>{inputs.numberOfMoulds}</strong> moulds.
                    </td>
                    <td rowSpan="2" colSpan="2" className="border border-gray-400 text-center p-2 font-bold">
                        {r(computed?.totalAbrasiveWeight)}
                    </td>
                    <td rowSpan="2" colSpan="2" className="border border-gray-400 text-center p-2 font-bold">
                        {r(computed?.totalBondWeight)}
                    </td>
                    <td colSpan="2" className="border border-gray-400 text-center p-2">
                        <strong>{r(computed?.kryoliteWeight)}</strong> gm <strong>Kryolite</strong>
                    </td>
                </tr>
                <tr>
                    <td colSpan="2" className="border border-gray-400 text-center p-2">
                        <strong>{r(computed?.pureBondWeight)}</strong> gm <strong>Bond {inputs.bondType}</strong>
                    </td>
                </tr>

                {/* Row 9 — Mould preparation */}
                <tr>
                    <td colSpan="9" className="border border-gray-400 p-3">
                        <table className="w-11/12 mx-auto border-none text-sm">
                            <tbody>
                                <tr>
                                    <td rowSpan="3" className="w-1/3 align-top pt-1 font-medium">MOULD PREPARATION</td>
                                    <td className="w-1/3 py-1.5">1. ____________________________</td>
                                    <td className="py-1.5">4. ____________________________</td>
                                </tr>
                                <tr>
                                    <td className="py-1.5">2. ____________________________</td>
                                    <td className="py-1.5">5. ____________________________</td>
                                </tr>
                                <tr>
                                    <td className="py-1.5">3. ____________________________</td>
                                    <td className="py-1.5">6. ____________________________</td>
                                </tr>
                                <tr>
                                    <td colSpan="2" className="pt-2">
                                        COPPER PLATED STEEL BAR (SIZE): ____________________________ = <strong>{copperBarTotal}</strong>
                                    </td>
                                    <td className="pt-2">
                                        TEMPERATURE: <strong>{computed?.sinteringTemperature ?? '—'} °C</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                </tr>

                {/* Row 10 — Pressures */}
                <tr>
                    <td colSpan="3" className="border border-gray-400 p-2">PROGRAMME NO.</td>
                    <td colSpan="3" className="border border-gray-400 p-2">
                        PRE-PRESSURE [P1] = <strong>{r(computed?.prePressure)} bar</strong>
                    </td>
                    <td colSpan="3" className="border border-gray-400 p-2">
                        MAIN-PRESSURE [P2] = <strong>{r(computed?.mainPressure)} bar</strong>
                    </td>
                </tr>

                {/* Operations rows */}
                {OPERATIONS.map((operation, index) => (
                    <tr key={index}>
                        <td className="pl-4 border border-gray-400 p-2" colSpan="2">{operation}</td>
                        <td className="border border-gray-400 p-2" />
                        <td className="border border-gray-400 p-2" />
                        <td className="border border-gray-400 p-2" />
                        <td colSpan="2" className="border border-gray-400 p-2" />
                        <td className="border border-gray-400 p-2" />
                        <td className="border border-gray-400 p-2" />
                    </tr>
                ))}

                {/* Signatures row */}
                <tr>
                    <td colSpan="9" className="border border-gray-400 p-2">
                        <div className="flex w-full">
                            <div className="w-1/2 h-12 flex items-start pl-4 pt-1 text-sm">ISSUED BY:</div>
                            <div className="w-1/2 h-12 flex items-start pl-4 pt-1 text-sm">APPROVED BY:</div>
                        </div>
                    </td>
                </tr>

            </tbody>
        </table>
    );
};

export default ProcessSheetTable;